import { useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { INCOME_MODULES, buildShopServiceList } from '../../lib/incomeExpenseDefaults';
import { sanitizeText, sanitizeUrl } from '../../utils/sanitize';

// Where the poster's starting data comes from: a price list ([{ name, price, category? }]) and the shop's own details.
// Both can be handed in by the caller (e.g. the Settings card passes the list exactly as it is on screen, saved or not);
// whatever is missing comes from the signed-in owner's settings and the selected shop's profile.

const MAX_SERVICES = 200;
const NAME_MAX = 80;
const MODULE_KEYS = new Set(INCOME_MODULES.map((m) => m.category));
const EMPTY = { services: [], shop: { name: '', phone: '', address: '', qrUrl: '', logoUrl: '' } };

/** One normaliser for every source of the list: text sanitized and capped, `category` a real module key, price a finite number. */
export function normaliseServices(raw) {
    if (!Array.isArray(raw)) return null;
    const list = [];
    for (const x of raw.slice(0, MAX_SERVICES)) {
        const name = sanitizeText(typeof x?.name === 'string' ? x.name : '', { maxLen: NAME_MAX });
        if (!name) continue;
        const n = x?.price === null || x?.price === undefined || x?.price === '' ? NaN : Number(x.price);
        list.push({
            name,
            price: Number.isFinite(n) ? n : null,
            category: typeof x?.category === 'string' && MODULE_KEYS.has(x.category) ? x.category : null,
        });
    }
    return list.length ? list : null;
}

/** The shop details on the poster: what the caller passes wins, the shop profile fills the rest. Only a shop's OWN public details —
 *  never the login phone or the default office name. */
export function normaliseShop(shop, bp) {
    const pick = (given, saved) => (given === undefined ? saved : given) || '';
    const logo = String(pick(shop?.logoUrl, bp?.shopLogo));
    return {
        name: sanitizeText(String(pick(shop?.name, bp?.shopName)), { maxLen: 80 }),
        phone: sanitizeText(String(pick(shop?.phone, bp?.shopPhone)), { maxLen: 30 }),
        address: sanitizeText(String(pick(shop?.address, bp?.businessAddress)), { maxLen: 160 }),
        qrUrl: sanitizeUrl(String(pick(shop?.qrUrl, bp?.facebookUrl)), { maxLen: 300 }),
        logoUrl: /^https?:\/\//i.test(logo) ? logo : '', // goes to the image proxy — an http(s) URL only
    };
}

/** { services, shop } for the editor. `enabled` = false (the window is closed) skips the work. */
export function useRateChartSource({ services, shop, enabled = true } = {}) {
    const user = useAuthStore((s) => s.user);
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);
    const bp = selectedOfficeSpace?.businessProfile;
    return useMemo(() => {
        if (!enabled) return EMPTY;
        return {
            services: normaliseServices(services) || normaliseServices(buildShopServiceList(user?.settings, bp)) || [],
            shop: normaliseShop(shop, bp),
        };
    }, [enabled, services, shop, user?.settings, bp]);
}
